import i18n from '@risksmart-app/i18n/src/i18n';
import type { IndicatorRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIndicatorsQuery,
  Indicator_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIndicatorsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useGetControlsRegister } from 'src/hooks/queries/control/useGetControlsRegister';
import { mapTrpcIndicatorsToGraphQL } from 'src/hooks/queries/indicator/useGetIndicatorRegister';
import { useLabelledFields as useControlLabelledFields } from 'src/pages/controls/useLabelledFields';
import { useLabelledFields as useIndicatorLabelledFields } from 'src/pages/indicators/useLabelledFields';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import {
  type IndicatorRowSlice,
  indicatorSideCategoryGetters,
  indicatorSideFieldConfig,
  prefixedLabel,
  resolveIndicatorSideRow,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type IndicatorControlRow = TableRecord &
  IndicatorRowSlice & {
    Id: string;
    ControlId: string;
    ControlFriendlyId: string;
    ControlTitle: string;
    ControlType: string;
    ControlDesignEffectiveness: string;
    ControlPerformanceEffectiveness: string;
    ControlOverallEffectiveness: string;
    ControlTestScheduleStatus: string;
  };

export default createDataSource({
  group: 'relationship',
  hasAccess: (_, __, isFeatureEnabled) =>
    isFeatureEnabled('relational-widgets'),
  parentTypes: [Parent_Type_Enum.Control, Parent_Type_Enum.Indicator],
  documentNode: GetIndicatorsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.indicator.register.queryOptions();
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: IndicatorRegisterResponse) =>
        mapTrpcIndicatorsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Indicator_Bool_Exp>(Parent_Type_Enum.Indicator),
  }),
  useTablePropsHook: (
    data: GetIndicatorsQuery | undefined,
    options: StatefulTableOptions<IndicatorControlRow>
  ) => {
    const fields = useMemo<TableFields<IndicatorControlRow>>(
      () => ({
        ControlFriendlyId: simpleField(
          prefixedLabel('control_one', i18n.t('columns.id'))
        ),
        ControlTitle: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.title'))
        ),
        ControlType: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.type'))
        ),
        ControlDesignEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.design_effectiveness')
          )
        ),
        ControlPerformanceEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.performance_effectiveness')
          )
        ),
        ControlOverallEffectiveness: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.effectiveness'))
        ),
        ControlTestScheduleStatus: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.testScheduleStatus')
          )
        ),
        ...indicatorSideFieldConfig<IndicatorControlRow>(),
      }),
      []
    );

    const indicatorsLabelled = useIndicatorLabelledFields(data?.indicator);
    const { data: controlData } = useGetControlsRegister({ queryArgs: {} });
    const controlsLabelled = useControlLabelledFields(controlData?.control);

    const joinedData = useMemo<IndicatorControlRow[] | undefined>(() => {
      if (!indicatorsLabelled || !controlsLabelled) {
        return undefined;
      }

      const controlMap = new Map(controlsLabelled.map((c) => [c.Id, c]));

      return indicatorsLabelled.flatMap((indicator) =>
        (indicator.parents ?? [])
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Control && p.parent.Id
          )
          .map((parent) => {
            const control = controlMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${indicator.Id}`,
              ControlId: parent.parent!.Id,
              ControlFriendlyId: control?.SequentialIdLabel ?? '',
              // Partner title falls back to '' (see note in indicatorRisk.ts).
              ControlTitle: control?.Title ?? '',
              ControlType: control?.Type ?? '',
              ControlDesignEffectiveness:
                control?.DesignEffectivenessLabelled ?? '',
              ControlPerformanceEffectiveness:
                control?.PerformanceEffectivenessLabelled ?? '',
              ControlOverallEffectiveness:
                control?.OverallEffectivenessLabelled ?? '',
              ControlTestScheduleStatus:
                control?.TestScheduleStatusLabelled ?? '',
              ...resolveIndicatorSideRow(indicator),
            };
          })
      );
    }, [indicatorsLabelled, controlsLabelled]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('control_indicator_other'),
      options
    );
  },
  entityNamePlural: 'control_indicator_other',
  entityNameSingular: 'control_indicator_one',
  fields: 'controlIndicators.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'controlFriendlyId',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('columns.id')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlFriendlyId'),
    },
    {
      id: 'controlTitle',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.title')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'controlType',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('controls.columns.type')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'controlDesignEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.design_effectiveness')
        ),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlDesignEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlDesignEffectiveness'
      ),
      ratingColourKey: 'design_effectiveness',
    },
    {
      id: 'controlPerformanceEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.performance_effectiveness')
        ),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlPerformanceEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlPerformanceEffectiveness'
      ),
      ratingColourKey: 'performance_effectiveness',
    },
    {
      id: 'controlOverallEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.effectiveness')),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlOverallEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlOverallEffectiveness'
      ),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'controlTestScheduleStatus',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.testScheduleStatus')
        ),
      categoryGetter: (item: IndicatorControlRow) =>
        item.ControlTestScheduleStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter(
        'ControlTestScheduleStatus'
      ),
    },
    ...indicatorSideCategoryGetters<IndicatorControlRow>(),
  ],
});
